import { useContext, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { find } from "lodash";
import { useQuery } from "@apollo/client/react";

import { KickOffContext } from "@/Contexts/KickOff";
import { CompanyResourceResourceTypeEnum, GetRepresentativesByClientDocument } from "@/graphql/generated/graphql";
import { SuperThin } from "@/theme";
import { ClientRepType } from "@/Contexts/KickOff/types";

type PendingRep = {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
};

const DEFAULT_PENDING: PendingRep = { id: "", name: "", email: "", phone: "", title: "" };

const ClientRepSection: React.FC = () => {
  const { selectedClientId, clientReps, setClientReps } = useContext(KickOffContext);

  const [pending, setPending] = useState<PendingRep>(DEFAULT_PENDING);

  const { data: repsData } = useQuery(GetRepresentativesByClientDocument, {
    skip: !selectedClientId,
    variables: { clientId: selectedClientId as string },
  });

  const existingReps = repsData?.getRepresentativesByClient ?? [];

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleRepSelect = (_: unknown, value: (typeof existingReps)[number] | string | null) => {
    if (!value) {
      setPending(DEFAULT_PENDING);
      return;
    }

    if (typeof value === "string") {
      setPending((prev) => ({ ...prev, id: "", name: value }));
      return;
    }

    const existing = find(existingReps, (r) => r.id === value.id);
    if (existing) {
      setPending({
        id: existing.id,
        name: existing.name,
        email: existing.resourceMeta?.email ?? "",
        phone: existing.resourceMeta?.primaryPhone ?? "",
        title: "",
      });
    }
  };

  const handleAddRep = () => {
    if (!pending.name.trim()) return;

    const newRep: ClientRepType = {
      id: pending.id || "0",
      name: pending.name,
      title: pending.title,
      resourceType: CompanyResourceResourceTypeEnum.Human,
      resourceMeta: {
        data: {
          email: pending.email,
          primaryPhone: pending.phone,
        },
      },
    };

    setClientReps((prev) => [...prev, newRep]);
    setPending(DEFAULT_PENDING);
  };

  const handleRemoveRep = (index: number) => {
    setClientReps((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <Divider sx={{ mb: 1.5 }} textAlign="left">
        <Typography variant="caption" color="text.secondary">
          Client Representatives
        </Typography>
      </Divider>

      {/* ── Pending rep form ─────────────────────────────────────────────── */}
      <Grid container spacing={1} alignItems="flex-start">
        {/* Name — autocomplete for existing client, freeSolo for new client */}
        <Grid size={3}>
          <Autocomplete
            options={existingReps}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.name
            }
            value={existingReps.find((r) => r.id === pending.id) ?? null}
            inputValue={pending.name}
            onInputChange={(_, value) => setPending((prev) => ({ ...prev, name: value, id: "" }))}
            onChange={handleRepSelect}
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="Rep Name" size="small" />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        <Grid size={3}>
          <TextField
            label="Email"
            size="small"
            fullWidth
            value={pending.email}
            onChange={(e) => setPending((prev) => ({ ...prev, email: e.target.value }))}
          />
        </Grid>

        <Grid size={2}>
          <TextField
            label="Phone"
            size="small"
            fullWidth
            value={pending.phone}
            onChange={(e) => setPending((prev) => ({ ...prev, phone: e.target.value }))}
          />
        </Grid>

        <Grid size={2}>
          <TextField
            label="Title (optional)"
            size="small"
            fullWidth
            value={pending.title}
            onChange={(e) => setPending((prev) => ({ ...prev, title: e.target.value }))}
          />
        </Grid>

        <Grid size={2} sx={{ display: "flex", alignItems: "center" }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!pending.name.trim()}
            onClick={handleAddRep}
            fullWidth
          >
            Add Rep
          </Button>
        </Grid>
      </Grid>

      {/* ── Added reps list ───────────────────────────────────────────────── */}
      {clientReps.length > 0 && (
        <List dense sx={{ mt: 1 }}>
          {clientReps.map((rep, index) => (
            <ListItem
              key={`rep-${index}-${rep.id}`}
              sx={{ border: "1px solid #e0e0e0", borderRadius: 1, mb: 0.5, pr: 1 }}
              secondaryAction={
                <IconButton size="small" onClick={() => handleRemoveRep(index)}>
                  <Delete fontSize="small" color="error" />
                </IconButton>
              }
            >
              <ListItemText
                primary={rep.name}
                secondary={
                  [
                    rep.title,
                    rep.resourceMeta?.data.email,
                    rep.resourceMeta?.data.primaryPhone,
                  ]
                    .filter(Boolean)
                    .join(" · ")
                }
                slotProps={{ primary: { sx: SuperThin } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ClientRepSection;
